import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle, useContext, useMemo } from 'react';
import { GoogleMap, MarkerF, OverlayViewF, OverlayView, useJsApiLoader, Polygon, Rectangle, Marker } from '@react-google-maps/api';
import Supercluster, { ClusterFeature, PointFeature } from 'supercluster';
import { AttractionData, GeographyBoundaryType, LocationData } from '../types/CustomType';
import { LOCATION_ACCURACY_CONFIG } from '../configuration';
import { Image, Button, Drawer } from 'antd';
import AttractionsSingle from './AttractionsSingle'
import { MapInfoContext } from '../context/mapInfoContext';
import AuthContext from '../context/authContext';
import { devLog } from '../utils/devLogger';
import {
  getCurrentUserLocation,
  getHighAccuracyUserLocation,
  getSmartUserLocation,
  getYongJingDefaultLocation,
  checkGeolocationSupport
} from '../utils/mapNavigationUtils';
//import GeographyBoundaryService from '../apis/GeographyBoundaryService';

const containerStyle = { width: '100%', height: '100%' };
const center = { lat: 23.92116545048729, lng: 120.54020492997185 };

const options = {
    maxZoom: 11,
    minZoom: 20
};



type Map = google.maps.Map & { zoom: number };

function formatDataToGeoJsonPoints(data: AttractionData[], isDrawOrder: boolean): GeoJSON.Feature<GeoJSON.Point>[] {

    return data.map((attraction) => ({

        type: 'Feature',
        geometry: { type: 'Point', coordinates: [Number(attraction.longitude), Number(attraction.latitude)] },
        properties: { cluster: false, ...attraction, attraction: attraction, isDrawOrder: isDrawOrder }
    }));
}

function getLabel(pointCount: number): google.maps.MarkerLabel {
    return { text: pointCount.toString(), color: '#353535', fontWeight: 'bold' };
}



export interface MapComponentDataProps {
    attractions: AttractionData[];
    isDrawOrder: boolean //是否要畫順序編號
    triggerCurrent: boolean;
    setMyLocation: (isMyLocation: boolean) => void;
    singleName: string | undefined;
    triggerSingle: boolean;
    onShowClusterAttractions?: (attractions: AttractionData[], title: string) => void;
}


const MapComponent_v2: React.FC<MapComponentDataProps> = ({ attractions, isDrawOrder, triggerCurrent, setMyLocation, triggerSingle, singleName, onShowClusterAttractions }) => {



    const mapContext = useContext(MapInfoContext);
    const authContext = useContext(AuthContext);

    const { isLoaded: isMapLoaded } = useJsApiLoader({ googleMapsApiKey: "AIzaSyAP0Ij24_Z323jesR6lBlXKowmHjJKoXcQ", language: "zh-TW" });
    const [zoom, setZoom] = useState<number>(options.maxZoom);
    const [bounds, setBounds] = useState<GeoJSON.BBox>([0, 0, 0, 0]);
    const [clusters, setClusters] = useState<ClusterFeature<any>[]>();
    const mapRef = useRef<Map>();
    const [allowGestures, setAllowGestures] = useState<boolean>(true); //手勢控制
    //當前位置
    const [currentPosition, setCurrentPosition] = useState<{ lat: number, lng: number } | null>(null);
    const [currentMarker, setcurrentMarker] = useState<google.maps.Marker | null>(null);
    const [currentQueryMarker, setcurrentQueryMarker] = useState<AttractionData>();




    const markerCluster = useMemo(() => {
        return new Supercluster({ radius: zoom > 13 ? 60 : 1000, maxZoom: zoom });
    }, [zoom]);

    const getUserLocation = async () => {
        if (!isMapLoaded || !mapRef.current) {
            console.warn("Google Maps 尚未載入完成，略過 getUserLocation");
            return;
        }

        devLog("🗺️ MapComponent_v2: 開始使用自動定位系統");

        // 使用 AuthContext 中的位置資訊
        const currentLocation = authContext.currentLocation;

        if (currentLocation) {
            const { lat, lng, accuracy, source } = currentLocation;

            devLog(`✅ MapComponent_v2: 使用已獲取的位置資訊`, {
                來源: source,
                座標: `${lat}, ${lng}`,
                精度: accuracy ? `${accuracy}m` : '未知'
            });

            setCurrentPosition({ lat, lng });

            // 根據精度和來源設定縮放等級
            let zoomLevel = 12;
            if (accuracy && accuracy <= 50) {
                zoomLevel = 15;
            } else if (source === 'gps') {
                zoomLevel = 13;
            }
            setZoom(zoomLevel);

            // 創建位置標記
            const marker = new google.maps.Marker({
                position: { lat, lng },
                map: mapRef.current,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: source === 'gps' && accuracy && accuracy <= 50 ? 12 : 10,
                    fillOpacity: 1,
                    strokeWeight: 2,
                    fillColor: source === 'gps' && accuracy && accuracy <= 50 ? '#00C851' :
                              source === 'gps' ? '#5384ED' : '#FFD653',
                    strokeColor: '#ffffff',
                },
            });

            setcurrentMarker(marker);
            geocodeLatLng(lat, lng);

        } else {
            // 如果 AuthContext 中沒有位置資訊，使用預設位置
            devLog("🏠 MapComponent_v2: AuthContext 中無位置資訊，使用永靖預設位置");
            const defaultLocation = getYongJingDefaultLocation();

            setCurrentPosition({ lat: defaultLocation.lat, lng: defaultLocation.lng });
            geocodeLatLng(defaultLocation.lat, defaultLocation.lng);
            setZoom(12);
        }
    };

    // const renderCount = useRef(0);
    // renderCount.current += 1;

    // useEffect(() => {
    //     devLog(`🔄 MapComponent_v2 被渲染次數: ${renderCount.current}`);
    // });



    const geocodeLatLng = (lat: number, lng: number) => {
        const geocoder = new google.maps.Geocoder();
        const latlng = { lat, lng };

        geocoder.geocode({ location: latlng }, (results, status) => {
            devLog("geocodeLatLng");

            if (status === "OK" && results && results.length > 0) {
                const addressComponents = results[0].address_components;

                let county = "";
                let town = "";

                addressComponents.forEach(component => {
                    if (component.types.includes("administrative_area_level_1")) {
                        county = component.long_name; // ex: 南投縣
                    }
                    if (component.types.includes("administrative_area_level_2")) {
                        town = component.long_name; // ex: 埔里鎮
                    }
                });

                if (county && town) {

                    // 呼叫你原本 API 查邊界資料
                    //fetch(`http://api.localhost/api/twgis?countyname=${county}&townname[]=${town}`)
                    fetch(`https://api.512better.org.tw/api/twgis?countyname=${county}&townname[]=${town}`)
                        .then(res => res.json())
                        .then(data => {
                            // 與原本相同的處理
                            //setGeoJsonData(data);
                            // ... 更新 map
                            if (mapRef.current) {
                                const map = mapRef.current;
                                map.data.forEach(feature => map.data.remove(feature));
                                map.data.addGeoJson(data);
                                map.data.setStyle({
                                    fillColor: 'transparent',
                                    strokeColor: '#000000',
                                    strokeWeight: 2,
                                    fillOpacity: 0.0,
                                });

                                const bounds = new google.maps.LatLngBounds();
                                data.features.forEach((feature: any) => {
                                    if (feature.geometry.type === 'MultiPolygon') {
                                        feature.geometry.coordinates.forEach((polygon: any[][]) => {
                                            polygon.forEach((ring: any[]) => {
                                                ring.forEach(([lng, lat]: [number, number]) => {
                                                    bounds.extend({ lat, lng });
                                                    //devLog("🏙️ 反查地區：", lat, lng);
                                                });
                                            });
                                        });
                                    }
                                });

                                map.fitBounds(bounds);
                            }
                        });
                }
            } else {
                console.error("無法反查地理位置:", status);
            }
        });
    };


    const prevAttractionsRef = useRef<AttractionData[] | null>(null);

    useEffect(() => {
        if (!attractions || attractions.length === 0) return;

        const prev = prevAttractionsRef.current;
        const prevCount = prev?.length ?? 0;

        //devLog("🎯 現在景點總數:", attractions.length, " 上次景點總數 :", prevCount);

        // ✅ 只在「景點總數不同」時觸發
        if (
            attractions.length !== prevCount && prevCount > 0 &&
            mapRef.current &&
            !triggerSingle
        ) {
            //devLog("🎯 景點總數不同，重新計算邊界");

            const bounds_ = new google.maps.LatLngBounds();
            attractions.forEach((a) => {
                bounds_.extend({ lat: +a.latitude, lng: +a.longitude });
            });

            const center = bounds_.getCenter();
            mapRef.current.panTo(center);
            mapRef.current.fitBounds(bounds_);
            setCurrentPosition({ lat: center.lat(), lng: center.lng() });
        }

        prevAttractionsRef.current = attractions;
    }, [attractions]);




    useEffect(() => {
        if (attractions.length && mapRef.current) {
        markerCluster.load(
            formatDataToGeoJsonPoints(attractions, isDrawOrder) as any[]
        );
        const bounds = mapRef.current.getBounds()?.toJSON();
        if (bounds) {
            const bbox: [number, number, number, number] = [
            bounds.west,
            bounds.south,
            bounds.east,
            bounds.north,
            ];
            setClusters(markerCluster.getClusters(bbox, zoom));
        }
        }
    }, [attractions, zoom]);



    useEffect(() => {
        if (triggerCurrent) {
            getUserLocation();
        } else {
            // 使用 AuthContext 中的位置資訊或預設位置
            const currentLocation = authContext.currentLocation;

            if (currentLocation) {
                devLog("🗺️ useEffect: 使用 AuthContext 中的位置資訊");
                setCurrentPosition({
                    lat: currentLocation.lat,
                    lng: currentLocation.lng
                });

                // 根據精度設定縮放等級
                if (currentLocation.accuracy && currentLocation.accuracy <= 50) {
                    setZoom(15);
                } else if (currentLocation.source === 'gps') {
                    setZoom(13);
                } else {
                    setZoom(12);
                }
            } else {
                // 使用預設位置
                devLog("🏠 useEffect: 使用永靖預設位置");
                const defaultLocation = getYongJingDefaultLocation();
                setCurrentPosition({
                    lat: defaultLocation.lat,
                    lng: defaultLocation.lng
                });
                setZoom(12);
            }

            currentMarker?.setMap(null);
        }
    }, [triggerCurrent, authContext.currentLocation]);



    useEffect(() => {


        if (triggerSingle) {

            for (let i = 0; i < attractions.length; i++) {
                if (attractions[i].title == singleName) {
                    setCurrentPosition({
                        lat: Number(attractions[i].latitude),
                        lng: Number(attractions[i].longitude)
                    });
                    setcurrentQueryMarker(attractions[i]);


                }
            }



        }

    }, [triggerSingle, attractions]);


    if (!isMapLoaded) return null;

    function handleClusterClick({ id, lat, lng }: { id: number, lat: number, lng: number }) {
        // 獲取聚合內的景點，設置 Infinity 以取得所有景點
        const clusterPoints = markerCluster.getLeaves(id, Infinity);
        const attractions = clusterPoints.map(point => point.properties.attraction);
        
        devLog("Cluster clicked:", id, "Attractions count:", attractions.length);
        
        // 調用父組件的顯示函數
        onShowClusterAttractions?.(attractions, `景點 (${attractions.length} 個)`);
        
        // 保留原有的地圖移動功能
        mapRef.current?.panTo({ lat, lng });
        
        // 可選：啟用自動縮放
        // const expansionZoom = Math.min(markerCluster.getClusterExpansionZoom(id), 20);
        // mapRef.current?.setZoom(expansionZoom);
    }







    function handleBoundsChanged() {
        if (mapRef.current) {
            const bounds = mapRef.current.getBounds()?.toJSON();
            setBounds([bounds?.west || 0, bounds?.south || 0, bounds?.east || 0, bounds?.north || 0]);
        }
    }



    const handleZoomChanged = () => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.zoom;

    // ✅ 限制 zoom 最小為 12
    if (currentZoom <= 12) {
      mapRef.current.setOptions({ minZoom: 12 });
    }

    // ✅ 動態變化 markerCluster 依據 zoom 調整 radius
    setZoom(currentZoom);
    mapContext.setMapZoom(currentZoom);
  };



    function handleMapLoad(map: google.maps.Map) {
        mapRef.current = map as Map;
        // 地圖初始化完成後觸發
        getUserLocation();
    }



    return (
        <>

            <GoogleMap
                onLoad={handleMapLoad}
                onBoundsChanged={handleBoundsChanged}
                onZoomChanged={handleZoomChanged}
                mapContainerStyle={containerStyle}
                options={{
                    mapTypeControl: false,
                    fullscreenControl: false,
                    //  scrollwheel: allowGestures,
                    gestureHandling: allowGestures ? "greedy" : "none",
                    mapId: "811459dd13aa7bc"  //811459dd13aa7bc(正式的) //27322436ed44a21(開發的)
                }
                }
                center={currentPosition || center}
                zoom={zoom}
            >


                {
                    (triggerSingle == false) ?

                        <>
                            {clusters?.map(({ id, geometry, properties }) => {
                                const [lng, lat] = geometry.coordinates;
                                const { cluster, point_count, title, available, marker_type, attraction, isDrawOrder, icon_slug } = properties;
                                // count = count + 1;
                                return cluster
                                    ? <MarkerF
                                        key={`cluster-${id}`}
                                        onClick={() => handleClusterClick({ id: id as number, lat, lng })}
                                        position={{ lat, lng }}
                                        icon="/images/cluster-pin.png"
                                        label={getLabel(point_count)} />
                                    :
                                    <React.Fragment key={`attraction-${properties.id}`}>

                                        <AttractionMarker
                                            key={`attraction-${properties.id}`}
                                            position={{ lat, lng }}
                                            title={attraction?.title}
                                            available={available}
                                            icon={icon_slug != null ? icon_slug : `https://storage.googleapis.com/yongjing-storge/default/map_icon/${marker_type}.png`}
                                            zoom={mapRef?.current?.zoom}
                                            attraction={attraction}
                                            isDrawOrder={isDrawOrder}
                                            orderID={`${attraction?.order}`}
                                        />
                                    </React.Fragment>

                            })}
                        </> :
                        <>
                            {
                                currentQueryMarker &&
                                <AttractionMarker
                                    key={`attraction-${currentQueryMarker?.uuid}`}
                                    position={{ lat: Number(currentQueryMarker.latitude), lng: Number(currentQueryMarker.longitude) }}
                                    title={currentQueryMarker?.title}
                                    available={true}
                                    icon={currentQueryMarker?.icon_slug != null ? currentQueryMarker?.icon_slug : `https://storage.googleapis.com/yongjing-storge/default/map_icon/${currentQueryMarker?.maker_type}.png`}
                                    zoom={mapRef?.current?.zoom}
                                    attraction={currentQueryMarker}
                                    isDrawOrder={false}
                                    orderID={`1`}
                                />
                            }
                        </>
                }



            </GoogleMap>

        </>

    )
}


interface AttractionMarker {
    available: boolean;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
    title: string;
    icon: string;
    attraction: AttractionData;
    isDrawOrder: false;
    orderID: string;
    zoom: any;
}



function getPixelPositionOffset(width: number, height: number): { x: number, y: number } {
    return { x: -(width / 2), y: -(height / 2) };
}

function shallowCompareAttractions(prev: AttractionData[], next: AttractionData[]): boolean {
    if (!Array.isArray(prev) || !Array.isArray(next)) return false;
    if (prev.length !== next.length) return false;
    return prev.every((a, i) => a.uuid === next[i].uuid);
}


function AttractionMarker({ position, available, title, icon, attraction, isDrawOrder, orderID, zoom }: AttractionMarker) {
    // const [visible, setVisible] = useState(false);
    // const buttonClass = available ? 'success' : 'warning';
    // const buttonText = available ? 'Book' : 'Reserved';

    // const imgUrl = + icon;
    const imgUrl = attraction.icon_slug != null ? attraction.icon_slug : `https://storage.googleapis.com/yongjing-storge/default/map_icon/${attraction.maker_type}.png`
    // icon_slug !=null ? icon_slug : https://storage.googleapis.com/yongjing-storge/default/map_icon/${marker_type}.png
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [drawerTitle, setDrawerTitle] = useState('');

    const showDrawer = (title: string) => {
        setDrawerTitle(title);
        openDrawer ? "" : setOpenDrawer(true);
    };

    const onCloseDrawer = () => {
        setOpenDrawer(false);
    };

    return <OverlayViewF
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={getPixelPositionOffset}>
        {

            <Drawer title={title} placement="right" onClose={onCloseDrawer} open={openDrawer} >
                {
                    attraction && <AttractionsSingle attraction={attraction} />
                }
            </Drawer>
        }

        <div className=' relative'>

            {(zoom > 16) ?

                <div
                    className=' absolute top-[-35px] left-[-50px] z-0 flex w-full'
                    onClick={() => showDrawer(`${attraction.title}`)}
                >
                    <div className=' px-2 py-1 mx-6 h-8 whitespace-nowrap font-semibold text-sm bg-white  text-primary rounded-full shadow-md flex items-center relative' >
                        <img src={imgUrl}
                            className='px-2 absolute left-[-40px] max-h-full max-w-full'
                            onClick={() => showDrawer(`${attraction.title}`)}
                        />
                        <div className='px-2'>{title}</div>
                        {isDrawOrder &&
                            <span className=' text-xl rounded-full w-6 h-6 text-center bg-fourth text-sixth  '>
                                {orderID}
                            </span>
                        }
                    </div>
                </div>

                :

                <div
                    className='absolute z-0 flex w-full'
                    onClick={() => showDrawer(`${attraction.title}`)}
                >
                    <div className='px-2 py-1 mx-6 h-8 whitespace-nowrap font-semibold text-sm   text-primary rounded-full shadow-md flex items-center relative' >
                        <img src={imgUrl}
                            className='px-2 absolute left-[-40px] max-h-full max-w-full'
                            onClick={() => showDrawer(`${attraction.title}`)}
                        />

                        {
                            (isDrawOrder && zoom >= 16) &&
                            <span className=' text-xl rounded-full w-6 h-6 text-center bg-fourth text-sixth  '>
                                {orderID}
                            </span>
                        }
                    </div>
                </div>
            }

        </div>
    </OverlayViewF>
}




export default React.memo(MapComponent_v2, (prevProps, nextProps) => {
    return shallowCompareAttractions(prevProps.attractions, nextProps.attractions)
        && prevProps.isDrawOrder === nextProps.isDrawOrder
        && prevProps.triggerCurrent === nextProps.triggerCurrent
        && prevProps.singleName === nextProps.singleName
        && prevProps.triggerSingle === nextProps.triggerSingle;
});
